"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LogClient = void 0;
var logging_1 = require("./logging");
var sender_1 = require("./sender");
var sessionId_1 = require("./sessionId");
var noop = function () { };
var LogClient = /** @class */ (function () {
    function LogClient(options) {
        var _a;
        this.metadata = {};
        this.onEvent = noop;
        this.stacksAlreadySeen = [];
        this.clientSequenceId = 0;
        this.eventNameMapping = {};
        this.eventLoggingPaused = false;
        this.eventLoggingPromise = Promise.resolve();
        this.eventLoggingResolve = noop;
        this.productEventBuffer = [];
        this.env = options.env;
        this.entityId = options.entityId;
        this.site = options.site;
        this.deployGroup = options.deployGroup;
        this.buildNumber = options.buildNumber;
        this.customEndpoint = options.customEndpoint;
        this.appVersion = options.appVersion;
        this.platform = options.platform || "web";
        this.disabledForTest = options.disabledForTest || false;
        this.eventNameMapping = options.eventNameMapping || {};
        this.getMappedEvent = (_a = options.getMappedEvent) !== null && _a !== void 0 ? _a : (function () { return undefined; });
        this.transformEventName = options.transformEventName;
        this.os = options.os;
        this.osVersion = options.osVersion;
        this.deviceId = options.deviceId;
        this.deviceModel = options.deviceModel;
        this.isWifi = options.isWifi;
        var mappingKeys = new Set(Object.keys(this.eventNameMapping));
        var isMappingValid = Object.values(this.eventNameMapping).filter(function (value) { return mappingKeys.has(value); }).length === 0;
        if (!isMappingValid) {
            /**
             * This is because we recursively call logEvent based on whether a mapping pair exists.
             * If we map an event to a value (legacy name) and that legacy name is in the map as a key (new name),
             * we run the risk of infinite recursion.
             */
            throw new Error("`options.eventNameMapping` is invalid, the same event name should not exist as both a key and a value");
        }
        if (options.disabledForTest) {
            this.sender = noop;
        }
        else if (options.send) {
            this.sender = options.send;
        }
        else if (this.env === "dev") {
            this.sender = function (path, data) {
                if (options.devParseStubbedLogs) {
                    var parsedData = void 0;
                    try {
                        parsedData = JSON.parse(data);
                    }
                    catch (e) {
                        parsedData = data;
                    }
                    (0, logging_1.plog)(path, parsedData);
                }
                else {
                    (0, logging_1.plog)("stubbed network request", path, data);
                }
            };
        }
        else {
            this.sender = sender_1.sendOverHttp;
        }
        if (options.sessionId) {
            (0, sessionId_1.set)(options.sessionId);
        }
    }
    LogClient.prototype.setIsWifi = function (isWifi) {
        this.isWifi = isWifi;
    };
    LogClient.prototype.setOsVersion = function (osVersion) {
        this.osVersion = osVersion;
    };
    LogClient.prototype.setMetadata = function (key, value) {
        this.metadata[key] = value;
    };
    LogClient.prototype.setEventLoggingPaused = function (isPaused) {
        var _this = this;
        if (this.eventLoggingPaused === isPaused)
            return;
        this.eventLoggingPaused = isPaused;
        if (isPaused) {
            this.eventLoggingPromise = new Promise(function (resolve) {
                _this.eventLoggingResolve = resolve;
            });
        }
        else {
            this.eventLoggingResolve();
            this.eventLoggingPromise = Promise.resolve();
            this.eventLoggingResolve = noop;
        }
    };
    LogClient.prototype.clearMetadata = function () {
        this.metadata = {};
    };
    LogClient.prototype.flush = function () {
        var _this = this;
        var _a;
        if (!((_a = this.productEventBuffer) === null || _a === void 0 ? void 0 : _a.length)) {
            return;
        }
        // As of 12/22/2020 in web-monorepo, product area is set simultaneously with logEvent
        // so we need to decorate it afterwards. Flush happens after a timeout which will defer execution.
        var productEvents = this.productEventBuffer.map(function (event) {
            var data = __assign(__assign({}, event.data), { productArea: _this.productArea });
            return __assign(__assign({}, event), { data: data });
        });
        this.productEventBuffer = [];
        this.sendToBackend("/logEvent", {
            events: productEvents.map(function (_a) {
                var data = _a.data;
                return data;
            }),
        });
        productEvents.forEach(function (_a) {
            var data = _a.data, resolver = _a.resolver;
            resolver();
            _this.onEvent(data);
        });
    };
    LogClient.prototype.logExposure = function (data) {
        if (typeof data === "string") {
            throw new Error("Must pass in an object");
        }
        var experimentName = data.experimentName, variant = data.variant;
        if (!experimentName || !variant) {
            throw new Error("Input must have keys `experimentName` and `variant`");
        }
        var transformedData = {
            eventName: "sawExperiment_" + experimentName,
            eventValue: "variant_" + variant,
        };
        return this.logProductEvent(transformedData);
    };
    LogClient.prototype.logProductEvent = function (rawData) {
        var _this = this;
        var _a;
        var data;
        if (typeof rawData === "string") {
            data = { eventName: rawData };
        }
        else {
            data = rawData;
        }
        // Double-write if an event name mapping exists
        var mappedEventName = (_a = this.getMappedEvent(data.eventName)) !== null && _a !== void 0 ? _a : this.eventNameMapping[data.eventName];
        if (mappedEventName) {
            this.logProductEvent(__assign(__assign({}, data), { eventName: mappedEventName }));
        }
        data = this.addProductEventDetails(data);
        var promise = new Promise(function (resolve) {
            _this.productEventBuffer.push({
                data: data,
                resolver: resolve,
            });
        });
        this.eventLoggingPromise.then(this.flush.bind(this));
        return promise;
    };
    LogClient.prototype.logDuration = function (data) {
        if (!data) {
            throw new Error("Requires data to log");
        }
        if (!this.entityId) {
            throw new Error("Need to set entityId to use");
        }
        if (data.name == null) {
            throw new Error("Requires 'name' to log duration");
        }
        if (data.duration == null) {
            throw new Error("Requires 'duration' to log duration");
        }
        return this.sendToBackend("/timeLog", data);
    };
    LogClient.prototype.logMetrics = function (metrics) {
        var payload = {
            site: this.site || "unknown",
            deployGroup: this.deployGroup || "unknown",
            metrics: metrics,
        };
        return this.sendToBackend("/metrics", payload);
    };
    LogClient.prototype.logMonsterverseProductEvents = function (events) {
        events = events.map(this.addProductEventDetails.bind(this));
        this.sendToBackend("/logEvent", { events: events, clientSentAt: new Date() });
    };
    LogClient.prototype.logMonsterversePerfStats = function (stats) {
        this.sendToBackend("/monsterversePerf", { events: Array.isArray(stats) ? stats : [stats] });
    };
    LogClient.prototype.logMonsterverseMessage = function (logs) {
        var _this = this;
        var logsArray = Array.isArray(logs) ? logs : [logs];
        var data = logsArray.map(function (log) { return (__assign(__assign(__assign({ type: "Log" }, _this.baseData()), { location: getLocation() }), log)); });
        this.sendToBackend("/monsterversePerf", { events: data });
    };
    LogClient.prototype.logException = function (error, info, extraAttributes) {
        if (this.stacksAlreadySeen.indexOf(error.stack || "") != -1) {
            return;
        }
        this.stacksAlreadySeen.push(error.stack || "");
        var context = Array.isArray(info) ? {} : info;
        var history = Array.isArray(info) ? info : context.history || [];
        delete context.history;
        var productArea = context.productArea;
        delete context.productArea;
        if (shouldLogError()) {
            (0, logging_1.warn)(error, error.stack, history, "[productArea=".concat(productArea, "]"));
        }
        // https://github.com/visionmedia/superagent/blob/ccecb4e94fc08b4555da9dbd9b96fa93678bc1f9/lib/client.js#L623-L632
        // add some extra data for these kinds of errors
        if (error.hasOwnProperty("crossDomain")) {
            var responseError = error;
            error.message =
                responseError.message +
                    " – status: " +
                    responseError.status +
                    ", method: " +
                    responseError.method +
                    ", url: " +
                    responseError.url;
        }
        var data = __assign(__assign({}, this.baseData()), { location: getLocation(), message: error.message, stack: error.stack, history: history, context: context, productArea: productArea, extraAttributes: extraAttributes });
        return this.sendToBackend("/frontendException", data);
    };
    LogClient.prototype.logMessage = function (logType, message, context) {
        var data = __assign(__assign({}, this.baseData()), { location: getLocation(), version: this.appVersion, env: this.env, logType: logType, message: message, context: context });
        return this.sendToBackend("/frontendSiteLog", data);
    };
    LogClient.prototype.logRequestError = function (type, request, response, info) {
        var context = Array.isArray(info) ? {} : info;
        var history = Array.isArray(info) ? info : context.history || [];
        delete context.history;
        var productArea = context.productArea;
        delete context.productArea;
        if (window.LOG_ALL_ERRORS) {
            (0, logging_1.warn)(type, request, response, history, "[productArea=".concat(productArea, "]"));
        }
        var data = __assign(__assign({}, this.baseData()), { location: getLocation(), type: type, request: request, response: response, history: history, context: context, productArea: productArea });
        return this.sendToBackend("/frontendApiError", data);
    };
    LogClient.prototype.handleError = function (msg) {
        if (this.env === "dev") {
            throw new Error(msg);
        }
        else {
            console.error(msg);
        }
    };
    LogClient.prototype.verifyAndFormatExperimentData = function (experiment) {
        if (!this.featureSwitches) {
            throw new Error("Unable to start experiment without being provided fetched list of feature switches");
        }
        var variant = this.featureSwitches[experiment];
        if (!variant) {
            this.handleError("Invalid experiment name");
        }
        if (variant === "off") {
            return null;
        }
        return {
            experiment: experiment,
            createdAt: new Date(),
            site: this.site,
            route: this.route,
            variant: variant,
            platform: this.platform,
            entityType: this.entityType,
            appVersion: this.appVersion,
        };
    };
    LogClient.prototype.logStartExperiment = function (experiment) {
        if (this.disabledForTest)
            return;
        var data = this.verifyAndFormatExperimentData(experiment);
        if (!data)
            return;
        var productEvent = {
            eventName: "startExperiment",
            eventValue: data.experiment,
        };
        this.onEvent(productEvent);
        return this.sendToBackend("/startExperiment", data);
    };
    LogClient.prototype.logConvertExperiment = function (experiment) {
        if (this.disabledForTest)
            return;
        var data = this.verifyAndFormatExperimentData(experiment);
        if (!data)
            return;
        var productEvent = {
            eventName: "convertExperiment",
            eventValue: data.experiment,
        };
        this.onEvent(productEvent);
        return this.sendToBackend("/convertExperiment", data);
    };
    LogClient.prototype.sendToBackend = function (path, data) {
        var endPoint;
        if (this.customEndpoint) {
            endPoint = this.customEndpoint;
        }
        else if (this.env === "testing-logclient") {
            endPoint = "http://localhost:3232";
        }
        else if (this.env === "dev-logclient") {
            endPoint = "http://localhost:8000";
        }
        else {
            endPoint = "https://logs.classdojo.com";
        }
        var toSend;
        toSend = JSON.parse(JSON.stringify(data));
        // Allowing override of entity so can start funnel for other user
        toSend.entityId = toSend.entityId || this.entityId;
        toSend.sessionId = (0, sessionId_1.get)();
        toSend = JSON.stringify(toSend);
        return this.sender(endPoint + path, toSend);
    };
    LogClient.prototype.baseData = function () {
        return {
            site: this.site,
            deployGroup: this.deployGroup,
            buildNumber: this.buildNumber,
            entityId: this.entityId,
            entityType: this.entityType,
            appVersion: this.appVersion,
            version: this.appVersion,
            env: this.env,
        };
    };
    LogClient.prototype.addProductEventDetails = function (event) {
        var _this = this;
        // Apply eventName transformation if needed
        event.eventName = this.transformEventName ? this.transformEventName(event.eventName) : event.eventName;
        // we don't want to overwrite keys passed in metadata with defaults
        event.metadata = __assign(__assign(__assign({}, this.metadata), { clientSequenceId: this.clientSequenceId, site: this.site }), event.metadata);
        this.clientSequenceId++;
        if (!event.entityId) {
            event.entityId = this.entityId;
        }
        if (!event.userType && this.entityType) {
            event.userType = this.entityType;
        }
        if (!event.sessionId) {
            event.sessionId = (0, sessionId_1.get)();
        }
        if (!event.appVersion && this.appVersion) {
            event.appVersion = this.appVersion;
        }
        if (!event.route && this.route) {
            event.route = this.route;
        }
        if (!event.path && this.path) {
            event.path = this.path;
        }
        if (!!this.featureSwitches && event.experiments) {
            event.variants = event.experiments.map(function (experiment) {
                return _this.featureSwitches ? _this.featureSwitches[experiment] : "unknown";
            });
        }
        if (!event.productArea && this.productArea) {
            event.productArea = this.productArea;
        }
        if (!event.os && this.os) {
            event.os = this.os;
        }
        if (!event.osVersion && this.osVersion) {
            event.osVersion = this.osVersion;
        }
        if (!event.deviceId && this.deviceId) {
            event.deviceId = this.deviceId;
        }
        if (!event.deviceModel && this.deviceModel) {
            event.deviceModel = this.deviceModel;
        }
        if (!event.isWifi && this.isWifi) {
            event.isWifi = this.isWifi;
        }
        return event;
    };
    return LogClient;
}());
exports.LogClient = LogClient;
function shouldLogError() {
    if (window.LOG_ALL_ERRORS) {
        return true;
    }
    var locationHash = window.location && window.location.hash;
    if (locationHash && /log_all_errors/i.test(locationHash)) {
        return true;
    }
    return false;
}
function getLocation() {
    return window.location ? window.location.href : undefined;
}
//# sourceMappingURL=client.js.map